import jwtDecode from 'jwt-decode';
import moment from 'moment';
import qs from 'qs';
import { formatCurrency } from '@sumup/intl';

import { get, isArray, isEmpty, isObject, isString, map, split, template, without } from 'lodash-es';

import CONFIG from '../Config';

const uuidv4 = require('uuid/v4');

//
export const stringifyQuery = (query, prefix = '', arrayFormat = 'bracket', encode = false) =>
  `${prefix}${qs.stringify(query, { arrayFormat, encode })}`;
export const parseQuery = query => {
  const peersString = query ? query.substr(1) : undefined;
  return qs.parse(peersString, { arrayFormat: 'bracket', encode: false });
};

// Url parser
export const parseURL = (url = '') => {
  const pathname = url.indexOf('?') > 0 ? url.substring(0, url.indexOf('?')) : url;
  const search = url.indexOf('?') > 0 ? `?${url.substring(url.indexOf('?') + 1)}` : '';

  return {
    pathname,
    search,
  };
};

// API
const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export const getAPIUrl = (url = '') => `${CONFIG.apiBaseUrl}${url}`;

export const getPathName = location => {
  const bn = CONFIG.baseNameUrl;
  const { pathname, search } = typeof location === 'string' ? parseURL(location) : location;
  const pathName =
    `${pathname ? pathname.replace(bn, '') : location}${typeof search === 'string' ? search : ''}` || '/';
  return pathName;
};

// JWT
const getTokenFromHeaders = req => {
  const {
    headers: { authorization },
  } = req;

  const [type, token] = split(authorization, ' ');
  if (type === 'Bearer') {
    return token;
  }
  return null;
};
const getTokenFromQuery = req => req.query.token;

const getTokenFromLocalStore = () => {
  try {
    // eslint-disable-next-line no-unused-vars
    const { authorization } = JSON.parse(localStorage.getItem('auth'));
    if (authorization) {
      return authorization.token;
    }
    return undefined;
  } catch (error) {
    // silent error
    return undefined;
  }
};

export const getToken = req => {
  const token = getTokenFromHeaders(req) || getTokenFromQuery(req) || getTokenFromLocalStore();
  return token;
};

export const setToken = req => {
  const token = getTokenFromHeaders(req) || getTokenFromQuery(req) || getTokenFromLocalStore();
  localStorage.setItem('auth', {
    ...JSON.parse(localStorage.getItem('auth')),
    authorization: { token },
  });
};

const getHeaders = extraHeaders => {
  const headers = { ...defaultHeaders, ...extraHeaders };
  const token = getTokenFromLocalStore();

  return token ? { ...headers, Authorization: token } : headers;
};

// saga
export const getOptions = ({ url, method, data, params, extraHeaders = {}, responseType }) => ({
  method: method || 'GET',
  url: getAPIUrl(url),
  data,
  params,
  headers: getHeaders(extraHeaders),
  json: true,
  crossdomain: true,
  withCredentials: true,
  ...(responseType ? { responseType } : {}),
});

export const getAlerts = response => {
  if (response.data.map) {
    return response.data.map(err => `[${response.status}] ${err}`);
  } else if (response.data.errors) {
    return response.data.errors.map(err => `[${response.status}] ${get(err, 'message') || get(err, 'data') || err}`);
  } else if (response.data.error) {
    if (isArray(response.data.error)) {
      return response.data.error.map(err => {
        if (get(err, 'message')) {
          return `[${response.status}] ${get(err, 'message')}`;
        }

        if (get(err, 'data')) {
          if (get(err, 'type') && get(err, 'data.code') && get(err, 'data.model')) {
            return `[${response.status}] ${get(err, 'type')} : ${get(err, 'data.model')} : ${get(err, 'data.code')}`;
          } else if (get(err, 'type')) {
            if (isString(get(err, 'data'))) {
              return `[${response.status}] ${get(err, 'type')} : ${get(err, 'data')}`;
            }

            const txtErr = map(get(err, 'data'), (value, key) => `${key} : ${value}`).join('; ');
            return `[${response.status}] ${get(err, 'type')} : ${txtErr}`;
          } else if (isString(get(err, 'data'))) {
            return `[${response.status}] ${get(err, 'data')}`;
          }

          return `[${response.status}] ${JSON.stringify(get(err, 'data'))}`;
        }

        return `[${response.status}] ${err}`;
      });
    } else {
      return [
        `[${response.status}] ${get(response, 'data.error.message') || get(response, 'data.error.data') || get(response, 'data.error')
        }`,
      ];
    }
  } else if (response.data.message) {
    return [`[${response.status}] ${response.data.message}`];
  }

  return [response.status];
};

export const processResponse = ({ response, error }) => {
  if (response) {
    const { data } = response;
    return { type: 'success', data };
  }

  let alerts = ['Unknown error: please, retry or contact your administrator'];
  if (typeof error.response !== 'undefined') {
    try {
      switch (get(error, 'response.status')) {
        case 401: {
          alerts = ['401 Unauthorized: please, retry or contact your administrator'];
          return { type: 'logout' };
        }
        case 403: {
          alerts = ['403 Forbidden: please, retry or contact your administrator'];

          if (get(error, 'response.data.message')) {
            return { type: 'forbidden', alerts: [`[${response.status}] ${get(error, 'response.data.message')}`] };
          }

          return { type: 'forbidden' };
        }
        case 404: {
          alerts = ['404 not found: please, retry or contact your administrator'];
          break;
        }
        case 500:
        case 506: {
          alerts = getAlerts(error.response);
          break;
        }
        default: {
          alerts = getAlerts(error.response);
        }
      }
    } catch (e) {
      return { type: 'fail', alerts: ['Unknown error: please, retry or contact your administrator'] };
    }
  }

  // if (error.message) {
  //   alerts = [error.message];
  // }

  return { type: 'fail', alerts };
};

// filters
export const applyFilter = ({ list, filters, rowName, defaultFilter }) => {
  let filteredList = list;
  if (filteredList && filteredList.size > 0) {
    filteredList = list.filter(row => {
      const sstrArr = filters[rowName] || defaultFilter;
      const str = row.get(rowName, '');
      return Array.isArray(sstrArr) && sstrArr.length > 0 ? sstrArr.some(sstr => String(str) === String(sstr)) : true;
    });
  }
  return filteredList;
};
export const applySearchFilter = ({ list, filters, rowNames }) => {
  let filteredList = list;
  if (filteredList && filteredList.size > 0) {
    filteredList = filteredList.filter(row => {
      const sstrArr =
        Array.isArray(rowNames) && rowNames.length > 0
          ? rowNames.map(rowName => (row.get('id') ? String(row.get(rowName)) : undefined))
          : [];
      const strs = filters.query ? filters.query.trim().split(/[ ,]+/) : [];
      if (strs.length === 0) return true;
      return sstrArr.some(sstr =>
        strs.some(str => {
          if (typeof str === 'undefined' || str === '') return true;
          return sstr.toUpperCase().includes(str.toUpperCase());
        })
      );
    });
  }
  return filteredList;
};

//
export const ciEquals = (a, b) =>
  typeof a === 'string' && typeof b === 'string'
    ? a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0
    : a === b;

export const extractBase64FromDataUrl = dataUrl => {
  const regex = /(data:.+base64,)(.+)/g;
  const m = regex.exec(dataUrl) || [];
  const base64 = m.length === 3 ? m[2] : undefined;
  return base64;
};

// HEADER
export const getAuthorization = headers => {
  const { authorization } = headers;
  try {
    return { token: authorization, ...jwtDecode(authorization) };
  } catch (error) {
    // silent error
    return { token: authorization };
  }
};
export const setLocalAuthorizationFromHeaders = headers => {
  const { authorization } = headers;
  try {
    localStorage.setItem('auth', {
      ...JSON.parse(localStorage.getItem('auth')),
      authorization: { token: authorization, ...jwtDecode(authorization) },
    });
  } catch (error) {
    localStorage.setItem('auth', undefined);
  }
};

export const getParamsFromSearch = search => {
  const hashes = search.split('?')[1] || '';
  const hash = hashes.split('&');
  const vars = {};
  for (let i = 0; i < hash.length; i += 1) {
    const [key, value] = hash[i].split('=');
    vars[key] = value;
  }
  return vars;
};

export const getAuthorizationFromParams = (location = {}) => {
  const { search } = location;
  if (search === '') return {};

  const { token } = getParamsFromSearch(search);
  try {
    return {
      authorization: { token: `Bearer ${token}`, ...jwtDecode(token) },
      isAuthenticated: true,
    };
  } catch (error) {
    // silent error catching
    return {};
  }
};
export const getThemeFromParams = (location = {}) => {
  const { search } = location;
  if (search === '') return undefined;

  const { theme } = getParamsFromSearch(search);
  try {
    return theme;
  } catch (error) {
    // silent error catching
    return undefined;
  }
};

export const getErrorMessage = error => {
  if (typeof error === 'string') return { t: Date.now(), msg: error };
  return { t: Date.now(), msg: get(error, 'message', JSON.stringify(error)) };
};

export const extractErrorMessages = errors => {
  let alerts = [getErrorMessage('Unknown error. Please retry or contact your administrator')];
  try {
    if (!errors) return alerts;
    if (Array.isArray(errors)) {
      alerts = errors.map(error => getErrorMessage(error));
    }
    if (get(errors, 'response')) {
      if (get(errors, 'response.status') === 401) {
        alerts = [getErrorMessage('username or password is incorrect')];
      }
      if (Array.isArray(get(errors, 'response.data.errors'))) {
        alerts = get(errors, 'response.data.errors').map(error => getErrorMessage(error));
      }
    }

    if (get(errors, 'error')) {
      if (Array.isArray(get(errors, 'error.errors'))) {
        alerts = get(errors, 'error.errors').map(error => {
          if (isEmpty(get(error, 'message')) && get(errors, 'code')) {
            return getErrorMessage(get(errors, 'code'));
          }

          return get(error, 'message.error_reason')
            ? getErrorMessage({ code: error.code, message: get(error, 'message.error_reason') })
            : getErrorMessage(error);
        });
      }
    }

    return alerts;
  } catch (error) {
    return alerts;
  }
};

export const getSuccessMessage = message => ({ t: Date.now(), msg: message });

export const computeMessage = ({ message, code, strings = {} }) => {
  const msg = get(strings, code, message);
  return { t: Date.now(), msg };
};

export const processErrors = ({ action, strings }) => {
  const t = Date.now();
  const defaultMessage = get(
    strings,
    'error.message.unknown',
    'Erreur inconnue. Veuillez contacter votre admninistrateur.'
  );
  const error = get(action, 'error', defaultMessage);
  const isArray = Array.isArray(error);

  const alerts = [];
  if (isArray) {
    // Display only the first error
    // TODO process all errors
    const msg =
      typeof get(error, '[0]') === 'string'
        ? get(error, '[0]', defaultMessage)
        : String(get(error, '[0]', defaultMessage));
    const alert = {
      t,
      msg,
    };
    alerts.push(alert);
  } else {
    const msg = typeof error === 'string' ? error : String(error);
    const alert = {
      t,
      msg,
    };
    alerts.push(alert);
  }
  return alerts;
};

const blobPdfFromBase64String = base64String => {
  const byteArray = Uint8Array.from(
    atob(base64String)
      .split('')
      .map(char => char.charCodeAt(0))
  );
  return new Blob([byteArray], { type: 'application/pdf' });
};

/**
 * Creates an anchor element `<a></a>` with
 * the base64 pdf source and a filename with the
 * HTML5 `download` attribute then clicks on it.
 * @param  {string} pdf
 * @return {void}
 */
export const downloadPDF = pdf => {
  const fileName = `${uuidv4()}.pdf`;

  if (navigator.msSaveBlob) {
    // IE11 & Edge
    const pdfBlob = blobPdfFromBase64String(pdf);
    navigator.msSaveBlob(pdfBlob, fileName);
  } else {
    const linkSource = `data:application/pdf;base64,${pdf}`;
    const downloadLink = document.createElement('a');
    downloadLink.target = 'self';
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  }
};

/**
 * Extract a local from a strings local list.
 * @param  {object} strings
 * @param  {string} key
 * @param  {string} defaultString
 * @return {string}
 */
export const getString = ({ strings, key, defaultString, options }) => {
  try {
    const esTemplate = get(strings, key, defaultString);
    const compiled = template(esTemplate);
    return compiled(options);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return defaultString;
  }
};

export const sanitizeQuotation = quotation => {
  const { loan = [], covered = [], ...rest } = quotation;
  return {
    ...rest,
    loan: without(loan, null),
    covered: without(covered, null),
  };
};

export const isDateAfter = (value1, value2) => {
  if (!value1) return false;

  const date1 = moment(value1, 'YYYY-MM-DD');
  const date2 = value2 ? moment(value2, 'YYYY-MM-DD') : moment().startOf('day');

  // eslint-disable-next-line no-restricted-globals
  return !isNaN(date1) && !isNaN(date2) && date1 >= date2;
};

export const isDateValid = date => {
  const regex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/i;
  const status = regex.test(date);
  return status;
};

/**
 * Merge default and dynamic saga watchers
 *
 * @param {object} watchers Watchers
 * @param {object} watchers.defaultWatchers Default watchers
 * @param {function} watchers.defaultWatchers.* Name and function of default watchers
 * @param {object} watchers.dynamicWatchers Customer's specific watchers
 * @param {function} watchers.dynamicWatchers.* Name and function of specific watchers
 *
 * @return {function[]} Merged list of default and dynamic saga watchers
 */
export const mergeWatchers = ({ defaultWatchers, dynamicWatchers }) => {
  const sagas = { ...defaultWatchers, ...dynamicWatchers };

  return Object.values(sagas);
};

/*
 * Return browser language in ISO language code (ISO_369-1 and ISO_3166-1)
 */
export const getNavigatorLanguage = () => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0];
  }

  return navigator.userLanguage || navigator.language || navigator.browserLanguage || 'fr-FR';
};

// TODO: Use an industrial phone number validator like "awesome-phonenumber" library
// => https://www.npmjs.com/package/awesome-phonenumber
// currently, it is not possible because we don't have the country code.
export const isPhone = phone => {
  if (isEmpty(phone)) return false;
  try {
    // Prefix : match 0033X, 0033(0)X, +33X, +33(0)X, 0X with X = 6 or 7
    const prefix = '(?:(?:\\+|00)33[\\s.-]{0,3}(?:\\(0\\)[\\s.-]{0,3})?|0)[6-7]';
    // French number syntax : match XX XX XX XX or XX XXX XXX
    const frenchSyntax = '(?:(?:[\\s.-]?\\d{2}){4}|\\d{2}(?:[\\s.-]?\\d{3}){2})';
    // Other number syntax : match at least one digit
    const otherSyntax = '([\\s.-]?\\d+)';

    // Regex : match french numbers that start by prefix or all other numbers that do not start by it
    const regex = new RegExp(`^(?:${prefix}${frenchSyntax}|(?!${prefix})${otherSyntax}+)$`, 'g');

    // if (phone.match(regex).length >= 10) {
    //   const phoneNumber = PhoneNumber(phone, 'FR');
    //   return phoneNumber.isValid();
    // }
    // return false;
    return phone.match(regex).length > 0;
  } catch (error) {
    return false;
  }
};

export const isEmail = email => {
  if (isEmpty(email)) return false;
  const regex =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
  const status = regex.test(email);
  return status;
};

export const formatDate = ({ date, lang = 'en', dateFormat }) => {
  if (typeof date === 'undefined') return undefined;
  moment.locale(lang);
  return moment(date).format(dateFormat);
};

/**
 * @description formatAmountCurrency - Formats a number according to the locale in the country's official
 *              curreny with support for various notations.
 * @param {number} value Examples: 12345.67, -0.89
 * @param {string | string[]} locales Optional - Examples: 'de-DE', 'DE', 'zh-Hans-CN', ['de-AT', 'de-DE'] /
 *                                               Default to 'fr-FR'
 * @param {string} currency Optional - Examples: 'EUR', 'BRL', 'USD'
 * @param {Intl.NumberFormatOptions} options Optional - Examples: { style: 'unit', unit: 'mile-per-hour' }
 */
export const formatAmountCurrency = (value, locales = 'fr-FR', currency = 'EUR', options) => {
  if (typeof value === 'number') {
    const formatedCurrency = formatCurrency(value, locales, currency, options);
    return formatedCurrency;
  }
  // try to converts to a number.
  if (typeof value === 'string') {
    const number = Number(value);
    const formatedCurrency = isNaN(number) ? value : formatCurrency(value, locales, currency, options);
    return formatedCurrency;
  }
  // not eligible to be formatted
  return value;
};

/**
 * @description formatPercent - format value to percent
 * @param {*} value Exemples: 0.014 => (1,4%)
 * @param {string | string[]} locales Optional - Examples: 'de-DE', 'DE', 'zh-Hans-CN', ['de-AT', 'de-DE'] -
 *                                               Default to 'fr-FR'
 * @param {Intl.NumberFormatOptions} options Optional - Examples: { style: 'unit', unit: 'mile-per-hour' }
 * @returns
 */
export const formatPercent = (value, locales = 'fr-FR', options = { maximumFractionDigits: 4 }) => {
  const nf = new Intl.NumberFormat(locales, { ...options, style: 'percent' });

  if (typeof value === 'number') {
    const percent = nf.format(value);
    return percent;
  }
  // try to converts value to a number.
  if (typeof value === 'string') {
    const number = Number(value);
    const percent = isNaN(number) ? value : `${nf.format(value)}`;
    return percent;
  }
  // not eligible to be formatted
  return value;
};

export const getDurationInMonths = ({ duration, durationUnit }) => {
  switch (durationUnit) {
    case 'year':
      return duration * 12;
    case 'month':
    default:
      return duration;
  }
};

export const convertDurationMonthsInUnit = ({ duration, durationUnit }) => {
  switch (durationUnit) {
    case 'year':
      return Math.round(duration / 12);
    case 'month':
    default:
      return duration;
  }
};

/**
 * Get label from strings, with Markdown if specified
 * @param {Object} data
 * @param {Object | string} [data.label={}] Label with code and default, or a string for backward compatibility
 * @param {string} data.label.code Code for the label
 * @param {string} data.label.default Default string if there's no localisation found for the code
 * @param {Object} strings Localisation strings
 * @param {string} defaultCode Default code if nothing if found with data.label
 * @param {string} defaultString Default string if nothing if found with data.label
 * @param {boolean} [markdown=false] With Markdown
 * @returns Label localised
 */
export const getLabel = ({ label, strings, defaultCode, defaultString, markdown = false }) => {
  if (markdown) {
    console.log('TODO markdown');
  }

  if (isObject(label)) {
    if (label.code != null || label.default != null) {
      if (label.code != null) {
        if (get(strings, label.code)) {
          return get(strings, label.code);
        }

        console.log(`Need localisation for ${label.code}`);
      }

      if (label.default != null) {
        return label.default;
      }

      console.log(`Label need a code or a default value ${label}`);
    }

    if (defaultCode != null && get(strings, defaultCode)) {
      console.log(`Need localisation (or at least a default value) for ${defaultCode}`);
      return get(strings, defaultCode);
    }

    if (defaultString != null) {
      console.log(`Need localisation (or at least a default value) for ${defaultString}`);
      return defaultString;
    }

    console.log(
      `Need localisation, default value or something for this... ${JSON.stringify(
        label
      )} :: ${defaultCode} :: ${defaultString}`
    );

    return label.code || '';
  }

  if (label != null) {
    if (label === '') {
      return label;
    }

    console.log(`This label should be changed to new format { code, default }: ${label}`);
    return get(strings, label, label);
  }

  if (defaultCode != null && get(strings, defaultCode)) {
    console.log(`Need a label and localisation for ${defaultCode}`);
    return get(strings, defaultCode);
  }

  if (defaultString != null) {
    if (defaultString === '') {
      return defaultString;
    }

    console.log(`Need a label and localisation for ${defaultString}`);
    return defaultString;
  }

  console.log(`Need a label, default value or something for this... ${label} :: ${defaultCode} :: ${defaultString}`);

  return '';
};

export const getNewTitle = ({ title, isTitleMultiple, idx }) => {
  if (isTitleMultiple) {
    return `${title}${idx}`;
  }

  return title;
};